import React, { useContext, useEffect, useState } from 'react';
import { Box, Button, IconButton, Typography, makeStyles } from '@material-ui/core';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';
import { useHistory, useLocation } from 'react-router-dom';

import { AuthContext } from '../../context/Auth/AuthContext';
import { useAiCredits } from '../../context/AiCredits/AiCreditsContext';
import { formatCredits } from '../AiCredits/utils';

const CREDITS_PATH = '/subscription/creditos';
// Página de créditos do super-admin: já mostra um banner de status inline no
// topo, então o toast seria redundante ali (mesma lógica do /subscription/creditos).
const SUPER_CREDITS_PATH = '/super-admin/ai-credits';

/** Dispensa vale para a sessão: fechar não some para sempre, volta no próximo login. */
const dismissKey = (level) => `aiCreditsAlert:dismissed:${level}`;

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'fixed',
    // Acima do conteúdo e do header (appBar 1100), abaixo de modais (1300):
    // um aviso persistente não pode cobrir o diálogo que o usuário abriu.
    zIndex: 1200,
    right: theme.spacing(3),
    bottom: theme.spacing(3),
    width: 380,
    maxWidth: 'calc(100vw - 32px)',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: '14px 16px',
    borderRadius: 12,
    boxShadow: '0 10px 30px rgba(15, 23, 42, 0.16)',
    animation: '$slideIn 200ms ease-out',
    [theme.breakpoints.down('sm')]: {
      right: theme.spacing(1.5),
      left: theme.spacing(1.5),
      width: 'auto',
      maxWidth: 'none',
      // Acima da barra de navegação mobile (76px) e da safe area do gesto
      bottom: 'calc(84px + env(safe-area-inset-bottom, 0px))',
    },
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  },
  '@keyframes slideIn': {
    from: { opacity: 0, transform: 'translateY(12px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  warn: {
    backgroundColor: '#fffbeb',
    border: '1px solid #fde68a',
    color: '#92400e',
  },
  danger: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#b91c1c',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
  },
  headerIcon: {
    fontSize: 22,
    flexShrink: 0,
    marginTop: 1,
  },
  title: {
    fontSize: '0.9rem',
    fontWeight: 700,
    color: 'inherit',
    lineHeight: 1.3,
  },
  message: {
    fontSize: '0.82rem',
    fontWeight: 500,
    color: 'inherit',
    lineHeight: 1.5,
    marginTop: 2,
  },
  closeBtn: {
    marginLeft: 'auto',
    marginTop: -6,
    marginRight: -8,
    // Alvo de toque mínimo (44px) mesmo com o ícone pequeno
    width: 44,
    height: 44,
    color: 'inherit',
    opacity: 0.7,
    '&:hover': { opacity: 1 },
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  ctaButton: {
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '0.8rem',
    borderRadius: 8,
    minHeight: 40,
    padding: '6px 16px',
    borderColor: 'currentColor',
    color: 'inherit',
  },
}));

const COPY = {
  low: {
    severity: 'warn',
    title: 'Créditos de IA acabando',
    build: (balance) =>
      `Restam ${formatCredits(balance)} do seu plano. Quando zerar, os agentes de IA param de responder.`,
  },
  plan_exhausted: {
    severity: 'warn',
    title: 'Créditos do plano esgotados',
    build: (balance) =>
      `O saldo do plano acabou — a IA segue usando seus créditos extras (${formatCredits(balance)}).`,
  },
  exhausted: {
    severity: 'danger',
    title: 'Créditos de IA esgotados',
    build: () => 'Os agentes de IA estão sem saldo e não respondem até a recarga.',
  },
};

/**
 * Aviso in-app de saldo de IA baixo/esgotado, com atalho para pedir mais.
 *
 * Só para quem pode resolver: proprietário da conta. Atendente não tem acesso à
 * área de assinatura, então avisar seria ruído sem saída.
 *
 * O SUPER ADMIN não vê este banner: o CTA é "Solicitar créditos" apontando para
 * a assinatura do tenant (/subscription/creditos) — ele pediria créditos a si
 * mesmo. O medidor de créditos na barra superior continua visível para ele.
 */
export default function AiCreditsAlert() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  // Fonte única: fetch + socket vivem no AiCreditsProvider, cujo gate já inclui
  // o super (canManageSubscription) e exige plano com crédito de plataforma.
  // Aqui só derivamos o que o banner mostra. A re-exibição ao piorar de nível é
  // natural: o dismiss é por nível (sessionStorage), então mudar de status
  // reavalia se aquele nível foi dispensado.
  const { enabled, credits } = useAiCredits();
  const status = credits?.status ?? null;
  const balance = credits?.planBalance ?? 0;
  const extraBalance = credits?.extraBalance ?? 0;

  const [dismissed, setDismissed] = useState(false);

  const copy = status ? COPY[status] : null;

  useEffect(() => {
    if (!status || !copy) return;
    setDismissed(sessionStorage.getItem(dismissKey(status)) === '1');
  }, [status, copy]);

  const handleDismiss = () => {
    if (status) sessionStorage.setItem(dismissKey(status), '1');
    setDismissed(true);
  };

  if (!enabled || !copy || dismissed) return null;
  // Banner é do tenant (CTA pede créditos na assinatura da empresa).
  if (user?.super) return null;
  // Na própria página de créditos o aviso já existe no topo do conteúdo
  // (vale para a aba do tenant e para a página do super-admin).
  if (
    location.pathname.startsWith(CREDITS_PATH) ||
    location.pathname.startsWith(SUPER_CREDITS_PATH)
  )
    return null;

  const isDanger = copy.severity === 'danger';
  const Icon = isDanger ? ErrorOutlineIcon : WarningAmberIcon;
  const shownBalance = status === 'plan_exhausted' ? extraBalance : balance;

  return (
    <Box
      className={`${classes.root} ${isDanger ? classes.danger : classes.warn}`}
      // Esgotado interrompe o serviço (assertivo); os demais são informativos
      role={isDanger ? 'alert' : 'status'}
      aria-live={isDanger ? 'assertive' : 'polite'}
    >
      <Box className={classes.header}>
        <Icon className={classes.headerIcon} aria-hidden="true" />
        <Box style={{ minWidth: 0 }}>
          <Typography className={classes.title}>{copy.title}</Typography>
          <Typography className={classes.message}>{copy.build(shownBalance)}</Typography>
        </Box>
        <IconButton
          size="small"
          className={classes.closeBtn}
          onClick={handleDismiss}
          aria-label="Dispensar aviso de créditos de IA"
        >
          <CloseIcon style={{ fontSize: 18 }} />
        </IconButton>
      </Box>
      <Box className={classes.actions}>
        <Button
          variant="outlined"
          size="small"
          className={classes.ctaButton}
          onClick={() => history.push(CREDITS_PATH)}
        >
          Solicitar créditos
        </Button>
      </Box>
    </Box>
  );
}
